import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router";
import { useVerificationStore, selectBatchLabel } from "../state/store";
import { usePreferences } from "@/hooks/usePreferences";
import { api } from "@/lib/api-client";

interface BatchDocument {
  id: string;
  filename: string;
  status: string;
  currentStep: string;
  classification: string | null;
}

function statusIcon(status: string): string {
  switch (status) {
    case "completed":
    case "exported":
      return "\u2713"; // checkmark
    case "error":
    case "rejected":
      return "\u2717"; // x
    case "held":
      return "\u23F8"; // pause
    case "ready":
    case "in_progress":
      return "\u25CF"; // dot
    default:
      return "\u25CB"; // empty dot
  }
}

function statusColor(status: string): string {
  switch (status) {
    case "completed":
    case "exported":
      return "#4CAF50";
    case "error":
    case "rejected":
      return "#F44336";
    case "held":
      return "#FFC107";
    case "ready":
    case "in_progress":
      return "#FFC107";
    default:
      return "var(--color-text-muted)";
  }
}

export function BatchTreeDrawer() {
  const navigate = useNavigate();
  const batchId = useVerificationStore((s) => s.batchId);
  const batchName = useVerificationStore((s) => s.batchName);
  // E-050 — drawer title leads with the unique batch identity; the free-text
  // name keeps operator value as the hover tooltip.
  const batchLabel = useVerificationStore(selectBatchLabel);
  const documentId = useVerificationStore((s) => s.documentId);
  const { preferences, updatePreference } = usePreferences();
  const listRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(preferences.batchTreePinned);
  const [isPinned, setIsPinned] = useState(preferences.batchTreePinned);
  const [docs, setDocs] = useState<BatchDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Sync pinned state from prefs on load
  useEffect(() => {
    setIsPinned(preferences.batchTreePinned);
    setIsOpen(preferences.batchTreePinned);
  }, [preferences.batchTreePinned]);


  // Load batch documents when open
  useEffect(() => {
    if (!isOpen || !batchId) return;
    setIsLoading(true);
    api
      .get<{ documents: BatchDocument[] }>(`/batches/${batchId}`)
      .then((data) => setDocs(data.documents ?? []))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [isOpen, batchId]);

  // Auto-scroll to active document
  useEffect(() => {
    if (!isOpen || !documentId || !listRef.current) return;
    const active = listRef.current.querySelector(".bt-doc-item--active");
    if (active) {
      active.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [isOpen, documentId, docs]);

  const togglePin = useCallback(() => {
    const next = !isPinned;
    setIsPinned(next);
    if (next) setIsOpen(true);
    updatePreference("batchTreePinned", next);
  }, [isPinned, updatePreference]);

  const toggleOpen = useCallback(() => {
    if (isPinned) return; // pinned stays open
    setIsOpen((v) => !v);
  }, [isPinned]);

  if (!batchId) return null;

  const completedCount = docs.filter(
    (d) => d.status === "completed" || d.status === "exported",
  ).length;

  return (
    <>
      {/* Tab handle — always visible on left edge */}
      {!isOpen && (
        <button
          className="bt-drawer-tab"
          onClick={() => setIsOpen(true)}
          title="Show batch tree"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6" />
          </svg>
          <span className="bt-drawer-tab-count">{docs.length > 0 ? `${completedCount}/${docs.length}` : ""}</span>
        </button>
      )}

      {/* Drawer panel */}
      <div className={`bt-drawer ${isOpen ? "bt-drawer--open" : ""}`}>
        <div className="bt-drawer-header">
          <span
            className="bt-drawer-title"
            title={batchName && batchName !== batchLabel ? batchName : undefined}
          >
            {batchLabel}
          </span>
          <div className="bt-drawer-actions">
            <button
              className={`bt-pin-btn ${isPinned ? "bt-pin-btn--active" : ""}`}
              onClick={togglePin}
              title={isPinned ? "Unpin drawer" : "Pin drawer open"}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill={isPinned ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                <path d="M12 2L12 12M12 12L8 8M12 12L16 8M5 21L19 21" />
              </svg>
            </button>
            {!isPinned && (
              <button className="bt-close-btn" onClick={toggleOpen} title="Close drawer">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="bt-drawer-progress">
          <div className="bt-drawer-progress-bar">
            <div
              className="bt-drawer-progress-fill"
              style={{ width: docs.length > 0 ? `${(completedCount / docs.length) * 100}%` : "0%" }}
            />
          </div>
          <span className="bt-drawer-progress-text">
            {completedCount}/{docs.length}
          </span>
        </div>

        <div className="bt-drawer-list" ref={listRef}>
          {isLoading && <div className="bt-drawer-loading">Loading...</div>}
          {docs.map((doc) => {
            const keyable = ["key", "verify", "qc", "supervisor"].includes(doc.currentStep);
            return (
              <button
                key={doc.id}
                className={`bt-doc-item ${doc.id === documentId ? "bt-doc-item--active" : ""} ${!keyable ? "bt-doc-item--disabled" : ""}`}
                onClick={() => keyable && navigate(`/verify/${doc.id}`)}
                disabled={!keyable}
                title={keyable ? `${doc.filename} — ${doc.currentStep}` : `${doc.filename} — ${doc.status} (not in keying stage)`}
              >
                <span className="bt-doc-status" style={{ color: statusColor(doc.status) }}>
                  {statusIcon(doc.status)}
                </span>
                <span className="bt-doc-name">{doc.filename}</span>
                <span className="bt-doc-step">{doc.currentStep}</span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
